import { Activity, ChevronLeft, ChevronRight, History, PanelRightClose } from "lucide-react";
import { useMemo, useState } from "react";

import type { ResultDocument } from "@rnavoyager/contracts";

import { TaskResultDocumentView } from "./TaskResultDocument";
import type { ResultSelection } from "./TaskResultDocument";

export function ResultWorkspaceRail({
  documents,
  finalized,
  live,
  onClose,
  onOpenWorkspace,
  onUseSelection,
  sessionId,
  publicDemo = false,
}: {
  documents: readonly ResultDocument[];
  finalized: boolean;
  live: boolean;
  onClose: () => void;
  onOpenWorkspace: () => void;
  onUseSelection?: (selection: ResultSelection) => void;
  sessionId: string;
  publicDemo?: boolean;
}) {
  const revisions = useMemo(() => normalizeRevisions(documents), [documents]);
  const [selectedRevision, setSelectedRevision] = useState<number | null>(null);
  const latestIndex = revisions.length - 1;
  const requestedIndex =
    selectedRevision === null
      ? latestIndex
      : revisions.findIndex((document) => document.revision === selectedRevision);
  const selectedIndex = requestedIndex >= 0 ? requestedIndex : latestIndex;
  const selected = revisions[selectedIndex];
  const followingLatest = selectedIndex === latestIndex;

  if (!selected) return null;

  function selectIndex(index: number) {
    const document = revisions[index];
    if (!document) return;
    setSelectedRevision(index === latestIndex ? null : document.revision);
  }

  return (
    <aside aria-label="结果工作台" className="workspace-right-rail result-workspace-rail">
      <header className="result-workspace-header">
        <div>
          <p>
            <History aria-hidden="true" size={13} /> RESULT WORKSPACE
          </p>
          <h2>{selected.title}</h2>
        </div>
        <div className="result-workspace-header-actions">
          <button
            aria-label="打开任务执行与文件树"
            className="workspace-rail-icon-button"
            onClick={onOpenWorkspace}
            title="任务执行与文件树"
            type="button"
          >
            <Activity size={16} />
          </button>
          <button
            aria-label="关闭结果工作台"
            className="workspace-rail-icon-button"
            onClick={onClose}
            title="关闭结果工作台"
            type="button"
          >
            <PanelRightClose size={16} />
          </button>
        </div>
      </header>

      <div className="result-workspace-toolbar">
        <span
          aria-live="polite"
          className={`result-workspace-status ${live && followingLatest ? "live" : "settled"}`}
        >
          {followingLatest ? (live ? "持续更新" : finalized ? "最终版本" : "最新快照") : "历史快照"}
        </span>
        <nav aria-label="结果版本" className="result-workspace-pager">
          <button
            aria-label="上一个结果版本"
            disabled={selectedIndex <= 0}
            onClick={() => selectIndex(selectedIndex - 1)}
            type="button"
          >
            <ChevronLeft size={15} />
          </button>
          <span>
            版本 {selected.revision}
            <small>
              {selectedIndex + 1}/{revisions.length}
            </small>
          </span>
          <button
            aria-label="下一个结果版本"
            disabled={selectedIndex >= latestIndex}
            onClick={() => selectIndex(selectedIndex + 1)}
            type="button"
          >
            <ChevronRight size={15} />
          </button>
        </nav>
      </div>

      <div className="result-workspace-body">
        <TaskResultDocumentView
          document={selected}
          key={`${selected.documentId}:${String(selected.revision)}`}
          onUseSelection={onUseSelection}
          sessionId={sessionId}
          inlineFeatured
          publicDemo={publicDemo}
        />
      </div>

      <footer className="result-workspace-footer">
        <span>文档</span>
        <code title={selected.documentId}>{selected.documentId}</code>
        <span>修订 {selected.revision}</span>
      </footer>
    </aside>
  );
}

export function normalizeRevisions(documents: readonly ResultDocument[]): ResultDocument[] {
  const revisions = new Map<number, ResultDocument>();
  for (const document of documents) revisions.set(document.revision, document);
  return [...revisions.values()].sort((left, right) => left.revision - right.revision);
}
